import React, { useState } from 'react';
import { PaperWrapper, Title } from '../common/styled';
import {
  ButtonContainer,
  HeaderWrapper,
  SearchContainer,
  StyledButton
} from './styled';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import CustomDatePicker from '../../components/DatePicker';
import dayjs, { Dayjs } from 'dayjs';
import DropdownSelect from '../../components/DropdownSelect';
import Icons from '../../components/icons';
import StaffAttendanceTable from './components/StaffAttendanceTable';
import { ModalMode, StaffAttendanceView } from './types';
import { useSelector } from 'react-redux';
import { RootState } from '../../redux/store';
import { SnackbarSeverityType, StaffAttendanceStatus } from '../../enums';
import ToastMessage from '../../components/ToastMessage';
import { Box, CircularProgress } from '@mui/material';
import StaffAttendanceModal from './components/StaffAttendanceModal';
import UploadStaffAttendanceModal from './components/UploadStaffAttendanceModal';
import { getStaffAttendanceRoles } from '../Staff/config/constant';
import { isEmpty } from 'lodash';
import { downloadReport } from '../../utils/reportUtils';
import staffAttendanceApis from '../../apis/staffAttendance';
import { transformAttendanceData } from './utils/transformStaffAttendanceData';

const StaffAttendancePage = () => {
  const [date, setDate] = useState<Dayjs | null>(null);
  const [role, setRole] = useState<string>('All');
  const [status, setStatus] = useState<StaffAttendanceStatus>(
    StaffAttendanceStatus.All
  );

  const [staffAttendanceData, setStaffAttendanceData] = useState<
    StaffAttendanceView[]
  >([]);
  const [selectedStaffAttendanceData, setSelectedStaffAttendanceData] =
    useState<StaffAttendanceView | null>(null);

  const [toastSeverity, setToastSeverity] = useState<SnackbarSeverityType>(
    SnackbarSeverityType.success
  );
  const [toastOpen, setToastOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState('');

  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [mode, setMode] = useState<ModalMode>(null);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState<boolean>(false);

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isDownloading, setIsDownLoading] = useState<boolean>(false);
  const [hasSearched, setHasSearched] = useState(false);

  const { user: loggedInUser } = useSelector(
    (state: RootState) => state.profile
  );

  const showToast = (message: string, severity: SnackbarSeverityType) => {
    setToastMessage(message);
    setToastSeverity(severity);
    setToastOpen(true);
  };

  const handleCloseToast = () => {
    setToastOpen(false);
  };

  const fetchAttendanceData = async () => {
    const formattedDate = date?.format('YYYY-MM-DD') ?? '';
    const response = await staffAttendanceApis.fetchStaffAttendanceData({
      school: loggedInUser?.school,
      date: formattedDate,
      role: role ? (role === 'All' ? '' : role) : '',
      status: status ? (status === 'All' ? '' : status) : ''
    });
    return response.data;
  };

  const fetchStaffAttendanceData = async (displayToast = true) => {
    try {
      const attendanceData = await fetchAttendanceData();
      setStaffAttendanceData(attendanceData);
      if (displayToast) {
        showToast(
          'Fetched staff attendance data successfully',
          SnackbarSeverityType.success
        );
      }
    } catch (error: any) {
      showToast(
        error?.errors?.[0]?.message ||
        'Error in fetching Staff Attendance Data',
        SnackbarSeverityType.error
      );
    }
  };

  const handleSearch = async () => {
    setIsLoading(true);
    setHasSearched(false);
    setStaffAttendanceData([]);
    await fetchStaffAttendanceData();
    setIsLoading(false);
    setHasSearched(true);
  };

  const handleDownload = async () => {
    setIsDownLoading(true);
    try {
      const attendanceData = await fetchAttendanceData();
      if (isEmpty(attendanceData)) {
        showToast(
          'There is no data available to download as an Excel file.',
          SnackbarSeverityType.error
        );
        return;
      }
      const transformedAttendanceData = transformAttendanceData(attendanceData);
      const fileNameParts = [
        `Staff_Attendance_Data_${dayjs(date).format('DD-MM-YYYY')}`,
        role !== 'All' ? role.toUpperCase() : '',
        status !== 'All' ? status : ''
      ].filter(Boolean);
      const fileName = fileNameParts.join('_');
      downloadReport(
        transformedAttendanceData,
        fileName,
        'Staff Attendance Data',
        showToast
      );
    } catch (error: any) {
      showToast(
        error?.errors?.[0]?.message ||
        'Error in downloading Staff Attendance Data',
        SnackbarSeverityType.error
      );
    } finally {
      setIsDownLoading(false);
    }
  };

  const handleEdit = (attendanceData: StaffAttendanceView) => {
    setSelectedStaffAttendanceData(attendanceData);
    setMode('edit');
    setIsModalOpen(true);
  };

  const handleOpenStaffAttendanceModal = (
    attendanceData?: StaffAttendanceView
  ) => {
    setSelectedStaffAttendanceData(attendanceData ?? null);
    setMode('add');
    setIsModalOpen(true);
  };

  const handleCloseStaffAttendanceModal = () => {
    setIsModalOpen(false);
    setSelectedStaffAttendanceData(null);
    setMode(null);
  };

  const handleOpenUploadModal = () => setIsUploadModalOpen(true);
  const handleCloseUploadModal = () => setIsUploadModalOpen(false);

  const isSearchDisabled = !date || isLoading;

  const attendanceStatusOptions = Object.entries(StaffAttendanceStatus).map(
    ([key, value]) => ({
      value: key,
      label: key === 'HALF_DAY_PRESENT' ? 'HALF DAY' : value.replace(/_/g, ' ')
    })
  );

  const staffRoles = [
    { value: 'All', label: 'All' },
    ...getStaffAttendanceRoles(loggedInUser?.school)
  ];

  return (
    <>
      <HeaderWrapper>
        <Title>Staff Attendance</Title>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <StyledButton
            startIcon={<Icons.PlusWhite />}
            onClick={() => handleOpenStaffAttendanceModal()}
          >
            Add Attendance
          </StyledButton>
          <StyledButton onClick={handleOpenUploadModal}>Upload</StyledButton>
        </Box>
      </HeaderWrapper>
      <PaperWrapper>
        <SearchContainer>
          <LocalizationProvider
            dateAdapter={AdapterDayjs}
            localeText={{
              fieldMonthPlaceholder: () => 'MMM'
            }}
          >
            <CustomDatePicker
              label='Date'
              value={date}
              format='DD MMM, YYYY'
              onChange={(value) => {
                setDate(value);
                setStatus(StaffAttendanceStatus.All);
                setRole('All');
                setStaffAttendanceData([]);
                setHasSearched(false);
              }}
              required
            />
          </LocalizationProvider>

          <DropdownSelect
            label='Role'
            placeholder='select role'
            value={role || ''}
            name='role'
            options={staffRoles}
            onChange={(e) => {
              setRole(e.target.value as string);
              setStatus(StaffAttendanceStatus.All);
              setStaffAttendanceData([]);
            }}
            sx={{ minWidth: 200 }}
          />

          <DropdownSelect
            label='Status'
            placeholder='select status'
            value={status || ''}
            name='status'
            options={attendanceStatusOptions}
            onChange={(e) => {
              setStatus(e.target.value as StaffAttendanceStatus);
              setStaffAttendanceData([]);
            }}
          />
        </SearchContainer>
        <ButtonContainer>
          <StyledButton
            startIcon={<Icons.SearchWhiteOutlined />}
            onClick={handleSearch}
            disabled={isSearchDisabled}
          >
            {isLoading ? <CircularProgress size={24} /> : 'Search'}
          </StyledButton>
          <StyledButton
            disabled={isDownloading || isSearchDisabled}
            onClick={handleDownload}
          >
            {isDownloading ? (
              <CircularProgress size={24} color='inherit' />
            ) : (
              'Download'
            )}
          </StyledButton>
        </ButtonContainer>
      </PaperWrapper>
      {hasSearched && (
        <StaffAttendanceTable
          searchDate={date}
          loading={isLoading}
          staffAttendanceData={staffAttendanceData}
          onEdit={handleEdit}
        />
      )}

      {isModalOpen && (
        <StaffAttendanceModal
          open={isModalOpen}
          onClose={handleCloseStaffAttendanceModal}
          mode={mode}
          selectedStaffAttendanceData={selectedStaffAttendanceData}
          showToast={showToast}
          fetchStaffAttendanceData={fetchStaffAttendanceData}
          searchDate={date}
        />
      )}

      {isUploadModalOpen && (
        <UploadStaffAttendanceModal
          open={isUploadModalOpen}
          onClose={handleCloseUploadModal}
          date={date}
        />
      )}

      <ToastMessage
        open={toastOpen}
        message={toastMessage}
        severity={toastSeverity}
        onClose={handleCloseToast}
      />
    </>
  );
};

export default StaffAttendancePage;
